// models/commerce/SupplierBox.js
import { Model } from 'pinia-orm';
import SupplierDailyOrderItem from './SupplierDailyOrderItem.js';
import { SupplierBoxState } from '@/enums/commerce/SupplierSignal.enum.js';

/**
 * Canonical JSON:API resource type for the supplier box-index card
 * (box-first design spec 2026-07-07, `../backend/_bmad-output/sync/briefs/
 * 2026-07-05-supplier-daily-orders-delivered.md`). One row per `box =
 * collection_plan × supplier`, visible only once the plan is CONFIRMED.
 *
 * ── LIST vs DETAIL SPLIT (design spec Section 6) ─────────────────────────────────
 * This model is the cheap, sparse-fieldset LIST projection
 * (`GET /supplier-boxes`, no `items[]`) — the card must answer "how much will
 * I be paid" and "is anything out of stock" without a nested fetch. The full
 * item set is loaded lazily on drill-down (`GET /supplier-boxes/{id}
 * ?include=items`) and lives on the `items` relation below.
 *
 * `invoiceableCents`/`oosCents`/`planRef` confirmed LIVE 2026-07-08 (`../backend/
 * _bmad-output/sync/briefs/2026-07-08-supplier-boxes-index-fields-delivered.md`,
 * replying to the 2026-07-07 FE→BE ask) — no client-side fallback needed.
 *
 * Wire attributes (camelCase):
 *   - collectionDate    string|null  the box's only identity (no name)
 *   - lifecycleState    int          SupplierBoxState: 1 AWAITING_PREP ·
 *                                    2 IN_COLLECTION · 3 RECEIVED · 4 CLOSED
 *   - itemsTotal         int         total line items on this box
 *   - itemsReserved      int         items with a RESERVED/PARTIALLY_RESERVED signal
 *   - itemsOosFlagged    int         items flagged OOS_FLAGGED
 *   - itemsCollected     int         items the collector has finalized
 *   - hasInvoice         bool        an invoice is attached (see SupplierBoxInvoice)
 *   - invoiceableCents   int         PIASTRES — live best-estimate payout, converges
 *                                    to the invoice total once RECEIVED/CLOSED *   - oosCents           int         PIASTRES — sum of OOS-flagged line amounts *   - membersServed      int         distinct members the club's orders from his shop served
 *                                    on this box (a count only — no identity)
 *   - planRef            string|number|null  member-safe short plan reference, *                                    shown only when collectionDate repeats *   - boxComment         string|null supplier's free-text comment on the whole box
 *   - writable           bool        BE write-gate truth (design spec Section 5, box-level)
 */
export const SUPPLIER_BOX_ENTITY = 'supplier-boxes';

export default class SupplierBox extends Model {
  static entity = SUPPLIER_BOX_ENTITY;

  static fields() {
    return {
      id: this.uid(),
      collectionDate: this.attr(null),
      lifecycleState: this.number(SupplierBoxState.AWAITING_PREP),
      itemsTotal: this.number(0),
      itemsReserved: this.number(0),
      itemsOosFlagged: this.number(0),
      itemsCollected: this.number(0),
      hasInvoice: this.boolean(false),
      invoiceableCents: this.number(0),
      oosCents: this.number(0),
      planRef: this.attr(null),
      membersServed: this.number(0),
      boxComment: this.attr(null),
      writable: this.boolean(true),
      // ── Detail-only, lazily populated on drill-down (design spec Section 6) ──
      // FK matches SupplierDailyOrderItem's existing CLIENT-ATTACHED `boxId`
      // field, not the resource name `supplier-boxes` — see that model's docs.
      items: this.hasMany(SupplierDailyOrderItem, 'boxId'),
    };
  }

  /**
   * True once the box has left the answerable window (RECEIVED/CLOSED share
   * the "closed" treatment — mirrors `deriveFrozenReason`'s threshold in
   * SupplierSignal.enum.js). Drives the money caption flip and the invoice
   * affordance gate.
   */
  get isClosed() {
    return Number(this.lifecycleState) >= SupplierBoxState.RECEIVED;
  }

  /**
   * Plain-language status pill key (design spec Section 1/Section 11) — 'awaiting' |
   * 'collecting' | 'done'. NEVER surface the raw `lifecycleState` enum in UI.
   */
  get statusKey() {
    if (this.isClosed) return 'done';
    if (Number(this.lifecycleState) === SupplierBoxState.IN_COLLECTION) return 'collecting';
    return 'awaiting';
  }

  /**
   * Money caption key (design spec Section 5.2) — 'open' | 'closed' | 'bought'.
   * Open → the live estimate. Closed with an invoice → the invoice total. Closed
   * with NO invoice → 'bought': the backend sends only the collected lines then
   * (BE brief 2026-10-19-supplier-box-finished-day-money.md), a past-tense fact,
   * never «المتوقّع تتحاسب عليه».
   */
  get moneyCaptionKey() {
    if (!this.isClosed) return 'open';
    return this.hasInvoice ? 'closed' : 'bought';
  }

  /** True when the closed-box invoice affordance should render (design spec Section 1/Section 8). */
  get hasInvoiceReady() {
    return this.isClosed && Boolean(this.hasInvoice);
  }
}
